/**
 * SVGRebelKitV2.jsx — Corporate Rebels Editorial System V2
 *
 * GRANULLO_REBEL_EDITORIAL_SYSTEM_V2
 * Authority: Founder Directive — Exact Code Implementation
 *
 * 10 deterministic rebel SVG components for structural editorial interruption.
 *
 * SVG Laws:
 * - strokeLinecap="round", strokeLinejoin="round"
 * - strokeWidth 3 (rebel overlays), slight asymmetry, human wobble
 * - Red system: var(--rebel-red) / #D92D20
 * - Ink system: var(--rebel-ink) / #0A0A0A
 * - CSS class "rebel-draw" for draw-on animation
 *
 * Components:
 * 1. RebelArrowDown
 * 2. RebelChartBar
 * 3. RebelFaqPlus
 * 4. RebelCircleHighlight
 * 5. RebelQuoteStrike
 * 6. RebelRedWordOverlay
 * 7. RebelSketchConnector
 * 8. RebelSectionNumber
 * 9. RebelMetricCrossout
 * 10. RebelChainRecurrence
 */

import React from 'react';

/* ═══ 1. REBEL ARROW DOWN ═══
   Hand-drawn wobbly arrow pointing down. Prevention route indicator. */

export function RebelArrowDown({ size = 48, color, className = '' }) {
  return (
    <svg
      width={size * 0.4} height={size} viewBox="0 0 20 48" fill="none"
      className={className} data-testid="rebel-arrow-down"
    >
      <path
        d="M11 4 C 9 12, 13 20, 10 28 S 8 36, 10 42"
        stroke={color || 'var(--rebel-ink)'}
        strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw"
      />
      <path
        d="M5 36 L10 44 L15 36"
        stroke={color || 'var(--rebel-ink)'}
        strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-1"
      />
    </svg>
  );
}

/* ═══ 2. REBEL CHART BAR ═══
   Hand-drawn bar chart overlay. Sketched metric visualization. */

export function RebelChartBar({ width = 120, height = 60, className = '' }) {
  return (
    <svg
      width={width} height={height} viewBox="0 0 120 60" fill="none"
      className={className} data-testid="rebel-chart-bar"
    >
      {/* Baseline */}
      <path
        d="M8 54 L112 54"
        stroke="var(--rebel-ink)" strokeWidth={2} strokeLinecap="round"
        className="rebel-draw"
      />
      {/* Bar 1 — short */}
      <rect x={14} y={38} width={14} height={16} rx={2}
        stroke="var(--rebel-ink)" strokeWidth={2.5} fill="var(--rebel-ink-fill)"
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-1"
      />
      {/* Bar 2 — tall */}
      <rect x={36} y={14} width={14} height={40} rx={2}
        stroke="var(--rebel-ink)" strokeWidth={2.5} fill="var(--rebel-ink-fill)"
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-2"
      />
      {/* Bar 3 — medium, RED (anomaly) */}
      <rect x={58} y={24} width={14} height={30} rx={2}
        stroke="var(--rebel-red)" strokeWidth={3} fill="none"
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-3"
      />
      {/* Bar 4 — short (prevented) */}
      <rect x={80} y={42} width={14} height={12} rx={2}
        stroke="var(--rebel-ink)" strokeWidth={2.5} fill="var(--rebel-ink-fill)"
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-4"
      />
      {/* X over red bar */}
      <path
        d="M60 22 L70 36 M70 22 L60 36"
        stroke="var(--rebel-red)" strokeWidth={2} strokeLinecap="round"
        className="rebel-draw rebel-draw-stagger-4"
      />
    </svg>
  );
}

/* ═══ 3. REBEL FAQ PLUS ═══
   Hand-drawn plus sign for FAQ expansion. Rotates 45deg on expand. */

export function RebelFaqPlus({ size = 24, expanded = false, className = '' }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      className={className} data-testid="rebel-faq-plus"
      style={{
        transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
        transition: 'transform 300ms ease',
      }}
    >
      <path
        d="M12 5 L12 19" stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round" className="rebel-draw"
      />
      <path
        d="M5 12 L19 12" stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round" className="rebel-draw rebel-draw-stagger-1"
      />
    </svg>
  );
}

/* ═══ 4. REBEL CIRCLE HIGHLIGHT ═══
   Hand-drawn wobbly circle for highlighting anomalies/blocked nodes. */

export function RebelCircleHighlight({ size = 48, className = '' }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 48 48" fill="none"
      className={className} data-testid="rebel-circle-highlight"
    >
      <path
        d="M24 6 C 34 5, 43 12, 42 24 S 36 43, 24 42 C 12 43, 3 36, 4 24 S 12 5, 24 6"
        stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw"
      />
    </svg>
  );
}

/* ═══ 5. REBEL QUOTE STRIKE ═══
   Scribbled underline / strikethrough for manifesto emphasis. */

export function RebelQuoteStrike({ width = 140, mode = 'underline', className = '' }) {
  const y = mode === 'strike' ? 12 : 22;
  return (
    <svg
      width={width} height={24} viewBox={`0 0 ${width} 24`} fill="none"
      className={className} data-testid="rebel-quote-strike"
    >
      <path
        d={`M4 ${y} C ${width * 0.2} ${y - 3}, ${width * 0.4} ${y + 2}, ${width * 0.6} ${y - 1} S ${width * 0.85} ${y + 2}, ${width - 4} ${y}`}
        stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round" strokeLinejoin="round"
        className="rebel-draw"
      />
    </svg>
  );
}

/* ═══ 6. REBEL RED WORD OVERLAY ═══
   Permanent Marker interruption word with rotation. */

export function RebelRedWordOverlay({ children, rotate = -2, offsetY = -18, className = '' }) {
  return (
    <span
      className={`inline-block ${className}`}
      data-testid="rebel-red-word"
      style={{
        fontFamily: '"Permanent Marker", cursive',
        color: 'var(--rebel-red)',
        letterSpacing: '0.01em',
        transform: `rotate(${rotate}deg) translateY(${offsetY}px)`,
        display: 'inline-block',
        position: 'relative',
      }}
    >
      {children}
    </span>
  );
}

/* ═══ 7. REBEL SKETCH CONNECTOR ═══
   Hand-drawn dashed connector between elements. */

export function RebelSketchConnector({ width = 100, direction = 'horizontal', className = '' }) {
  if (direction === 'vertical') {
    return (
      <svg
        width={20} height={width} viewBox={`0 0 20 ${width}`} fill="none"
        className={className} data-testid="rebel-sketch-connector"
      >
        <path
          d={`M10 4 C 8 ${width * 0.25}, 13 ${width * 0.5}, 9 ${width * 0.75} S 11 ${width - 8}, 10 ${width - 4}`}
          stroke="var(--rebel-ink)" strokeWidth={2}
          strokeLinecap="round" strokeDasharray="6 5"
          className="rebel-draw"
        />
      </svg>
    );
  }
  return (
    <svg
      width={width} height={20} viewBox={`0 0 ${width} 20`} fill="none"
      className={className} data-testid="rebel-sketch-connector"
    >
      <path
        d={`M4 10 C ${width * 0.25} 7, ${width * 0.5} 13, ${width * 0.75} 9 S ${width - 8} 11, ${width - 4} 10`}
        stroke="var(--rebel-ink)" strokeWidth={2}
        strokeLinecap="round" strokeDasharray="6 5"
        className="rebel-draw"
      />
    </svg>
  );
}

/* ═══ 8. REBEL SECTION NUMBER ═══
   Red hand-drawn numbering prefix (01–07). Permanent Marker. */

export function RebelSectionNumber({ number = '01', size = 'md', className = '' }) {
  const sizes = { sm: 'text-[18px]', md: 'text-[24px]', lg: 'text-[32px]' };
  return (
    <span
      className={`inline-block ${sizes[size] || sizes.md} ${className}`}
      data-testid="rebel-section-number"
      style={{
        fontFamily: '"Permanent Marker", cursive',
        color: 'var(--rebel-red)',
        letterSpacing: '0.02em',
        lineHeight: 1,
      }}
    >
      {String(number).padStart(2, '0')}
    </span>
  );
}

/* ═══ 9. REBEL METRIC CROSSOUT ═══
   Big X crossout over prevented misuse losses. */

export function RebelMetricCrossout({ width = 80, height = 40, className = '' }) {
  return (
    <svg
      width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none"
      className={className} data-testid="rebel-metric-crossout"
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
    >
      <path
        d={`M4 4 L${width - 4} ${height - 4}`}
        stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round"
        className="rebel-draw"
      />
      <path
        d={`M${width - 4} 4 L4 ${height - 4}`}
        stroke="var(--rebel-red)" strokeWidth={3}
        strokeLinecap="round"
        className="rebel-draw rebel-draw-stagger-1"
      />
    </svg>
  );
}

/* ═══ 10. REBEL CHAIN RECURRENCE ═══
   Scribbled recurrence marks over repeated structural failures. "same pattern" callout. */

export function RebelChainRecurrence({ width = 160, label = 'same pattern', className = '' }) {
  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="rebel-chain-recurrence"
    >
      <svg
        width={width} height={28} viewBox={`0 0 ${width} 28`} fill="none"
      >
        {/* Wobbly strike-through line */}
        <path
          d={`M4 14 C ${width * 0.15} 10, ${width * 0.3} 18, ${width * 0.5} 13 S ${width * 0.7} 16, ${width - 4} 14`}
          stroke="var(--rebel-red)" strokeWidth={3}
          strokeLinecap="round" strokeLinejoin="round"
          className="rebel-draw"
        />
        {/* Small tick marks */}
        <path d={`M${width * 0.25} 6 L${width * 0.25} 22`} stroke="var(--rebel-red)" strokeWidth={2} strokeLinecap="round" className="rebel-draw rebel-draw-stagger-1" />
        <path d={`M${width * 0.5} 6 L${width * 0.5} 22`} stroke="var(--rebel-red)" strokeWidth={2} strokeLinecap="round" className="rebel-draw rebel-draw-stagger-2" />
        <path d={`M${width * 0.75} 6 L${width * 0.75} 22`} stroke="var(--rebel-red)" strokeWidth={2} strokeLinecap="round" className="rebel-draw rebel-draw-stagger-3" />
      </svg>
      <span
        style={{
          fontFamily: '"Kalam", cursive',
          color: 'var(--rebel-red)',
          fontSize: '14px',
          fontWeight: 700,
        }}
      >
        {label}
      </span>
    </div>
  );
}


/**
 * 11. RebelMarkerArrow — Corporate Rebels single-stroke hand-drawn arrow
 * GRANULLO_REBEL_ARROW_SYSTEM_V1
 *
 * One main curved stroke + two short arrowhead strokes.
 * NO fill. Round caps. Round joins. Slight asymmetry. Editorial simplicity.
 */
export function RebelMarkerArrow({
  className = "",
  dark = false,
  size = 160,
  rotate = -4,
  opacity = 0.88,
}) {
  const stroke = dark ? "#D0D0D0" : "#0A0A0A";

  return (
    <svg
      viewBox="0 0 160 160"
      width={size}
      height={size}
      className={`rebel-marker-arrow ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ opacity, transform: `rotate(${rotate}deg)` }}
    >
      {/* Main curved marker stroke */}
      <path
        d="M42 18 C18 58, 22 104, 88 132"
        stroke={stroke}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="rebel-draw"
      />
      {/* Hand-drawn arrow head — two short strokes */}
      <path
        d="M88 132 L70 120"
        stroke={stroke}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-1"
      />
      <path
        d="M88 132 L86 108"
        stroke={stroke}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="rebel-draw rebel-draw-stagger-2"
      />
    </svg>
  );
}
